import { useState, useEffect, useMemo } from 'react';
import { BrowserLinearService } from '@/services/linear-service';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightArrowLeft,
  faArrowUpRightFromSquare,
  faBolt,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faCircleCheck,
  faCircleExclamation,
  faClock,
  faComment,
  faFileLines,
  faLayerGroup,
  faLink,
  faSpinner,
  faUser,
  faUsers,
  faXmark
} from '@fortawesome/free-solid-svg-icons';

const mkIcon = (def: import('@fortawesome/fontawesome-svg-core').IconDefinition) =>
  ({ className = '' }: { className?: string }) =>
    <FontAwesomeIcon icon={def} className={className} />;

interface LinearIssueDetailProps {
  issueIdentifier: string;
  apiKey: string;
  onClose: () => void;
  onNavigate?: (issueIdentifier: string) => void;
}

function getLinearStatusColor(stateName: string, stateColor?: string): string {
  if (stateColor) {
    return `border-[${stateColor}]/30 text-[${stateColor}]`;
  }
  const s = stateName.toLowerCase();
  if (s.includes('done') || s.includes('completed') || s.includes('merged')) return 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30';
  if (s.includes('in progress') || s.includes('started') || s.includes('review')) return 'bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-500/30';
  if (s.includes('backlog') || s.includes('triage') || s.includes('todo')) return 'bg-slate-500/20 text-slate-600 dark:text-slate-300 border-slate-500/30';
  if (s.includes('cancelled') || s.includes('canceled') || s.includes('duplicate')) return 'bg-red-500/20 text-red-700 dark:text-red-300 border-red-500/30';
  return 'bg-slate-500/20 text-slate-600 dark:text-slate-300 border-slate-500/30';
}

function getPriorityInfo(priority: number): { label: string; color: string } {
  switch (priority) {
    case 0: return { label: 'No priority', color: 'text-gray-400 dark:text-slate-500' };
    case 1: return { label: 'Urgent', color: 'text-red-500' };
    case 2: return { label: 'High', color: 'text-orange-500' };
    case 3: return { label: 'Medium', color: 'text-yellow-500' };
    case 4: return { label: 'Low', color: 'text-blue-400' };
    default: return { label: `P${priority}`, color: 'text-gray-400 dark:text-slate-500' };
  }
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatDateShort(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function timeAgo(d: string): string {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return formatDateShort(d);
}

// Simple markdown to HTML (Linear descriptions are markdown)
function markdownToHtml(md: string): string {
  if (!md) return '';
  let html = md
    // Code blocks
    .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Bold
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Strikethrough
    .replace(/~~(.+?)~~/g, '<s>$1</s>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    // Headers
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // Unordered lists
    .replace(/^[-*] (.+)$/gm, '<li>$1</li>')
    // Ordered lists
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    // Blockquotes
    .replace(/^> (.+)$/gm, '<blockquote>$1</blockquote>')
    // Horizontal rules
    .replace(/^---$/gm, '<hr />')
    // Line breaks → paragraphs
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br />');
  // Wrap consecutive <li> in <ul>
  html = html.replace(/((?:<li>.*?<\/li>\s*)+)/g, '<ul>$1</ul>');
  return `<p>${html}</p>`;
}

const PersonBadge = ({ name, avatarUrl, label }: { name?: string; avatarUrl?: string; label: string }) => {
  if (!name) return <span className="text-xs text-gray-500 dark:text-slate-500">Unassigned</span>;
  return (
    <div className="flex items-center gap-2">
      {avatarUrl ? <img src={avatarUrl} alt="" className="h-6 w-6 rounded-full" /> : <FontAwesomeIcon icon={faUser} className="h-5 w-5 text-gray-400 dark:text-slate-500" />}
      <div>
        <div className="text-xs text-gray-900 dark:text-white font-medium">{name}</div>
        <div className="text-[10px] text-gray-500 dark:text-slate-500">{label}</div>
      </div>
    </div>
  );
};

const SectionHeader = ({ id, title, icon: Icon, count, expanded, onToggle }: {
  id: string; title: string; icon: any; count?: number; expanded: boolean; onToggle: (id: string) => void;
}) => (
  <button onClick={() => onToggle(id)}
    className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-gray-100 dark:hover:bg-slate-700/20 transition-colors border-b border-gray-200 dark:border-slate-700/20">
    {expanded ? <FontAwesomeIcon icon={faChevronDown} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" /> : <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-500" />}
    <Icon className="h-4 w-4 text-gray-500 dark:text-slate-400" />
    <span className="text-sm font-medium text-gray-900 dark:text-white">{title}</span>
    {count !== undefined && count > 0 && (
      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-300">{count}</span>
    )}
  </button>
);

const IssueLinkBtn = ({ identifier, summary, status, onClick }: {
  identifier: string; summary?: string; status?: { name: string; color?: string }; onClick: (id: string) => void;
}) => (
  <button onClick={() => onClick(identifier)}
    className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700/30 transition-colors text-left w-full group">
    <span className="text-xs font-mono text-indigo-600 dark:text-indigo-400 group-hover:text-indigo-500 dark:group-hover:text-indigo-300 flex-shrink-0">{identifier}</span>
    {status && <span className={`text-[10px] px-1.5 py-0.5 rounded border flex-shrink-0 ${getLinearStatusColor(status.name, status.color)}`}>{status.name}</span>}
    {summary && <span className="text-xs text-gray-600 dark:text-slate-300 truncate">{summary}</span>}
  </button>
);

export function LinearIssueDetail({ issueIdentifier, apiKey, onClose, onNavigate: _onNavigate }: LinearIssueDetailProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [issue, setIssue] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['description', 'details', 'people', 'comments', 'children', 'relations'])
  );
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  // Internal navigation
  const [currentId, setCurrentId] = useState(issueIdentifier);
  const [navHistory, setNavHistory] = useState<string[]>([]);

  const service = useMemo(() => new BrowserLinearService({ apiKey }), [apiKey]);

  useEffect(() => {
    setCurrentId(issueIdentifier);
    setNavHistory([]);
  }, [issueIdentifier]);

  useEffect(() => {
    fetchIssue();
  }, [currentId]);

  const fetchIssue = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await service.getIssueDetail(currentId);
      if (!data) {
        setError('Issue not found');
      } else {
        setIssue(data);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load issue');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section); else next.add(section);
      return next;
    });
  };

  const handleIssueClick = (id: string) => {
    setNavHistory(prev => [...prev, currentId]);
    setCurrentId(id);
    setDescriptionExpanded(false);
  };

  const handleBack = () => {
    const prev = [...navHistory];
    const backId = prev.pop();
    if (backId) {
      setNavHistory(prev);
      setCurrentId(backId);
      setDescriptionExpanded(false);
    }
  };

  const labels = issue?.labels?.nodes || [];
  const children = issue?.children?.nodes || [];
  const relations = issue?.relations?.nodes || [];
  const comments = issue?.comments?.nodes || [];
  const attachments = issue?.attachments?.nodes || [];
  const history = issue?.history?.nodes || [];

  return (
    <div className="bg-white/95 dark:bg-slate-800/60 border-t border-b border-indigo-500/20 animate-in slide-in-from-top-1 duration-200">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-slate-800/80">
        <div className="flex items-center gap-2 min-w-0">
          {navHistory.length > 0 && (
            <button onClick={handleBack} className="p-1 hover:bg-gray-200 dark:hover:bg-slate-700 rounded transition-colors flex-shrink-0" title="Back">
              <FontAwesomeIcon icon={faChevronLeft} className="h-3.5 w-3.5 text-indigo-400" />
            </button>
          )}
          {loading ? (
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 text-indigo-400 animate-spin" />
              <span className="text-xs text-gray-500 dark:text-slate-400">Loading {currentId}...</span>
            </div>
          ) : issue ? (
            <>
              <img src="/brands/linear/dark.svg" className="h-4 w-4 flex-shrink-0 dark:hidden" alt="" />
              <img src="/brands/linear/light.svg" className="h-4 w-4 flex-shrink-0 hidden dark:block" alt="" />
              <span className="text-xs font-mono text-indigo-600 dark:text-indigo-400 flex-shrink-0">{issue.identifier}</span>
              <span className="text-xs font-semibold text-gray-900 dark:text-white truncate">{issue.title}</span>
              {navHistory.length > 0 && (
                <span className="text-[10px] text-gray-500 dark:text-slate-600 flex-shrink-0">← {navHistory[navHistory.length - 1]}</span>
              )}
            </>
          ) : (
            <span className="text-xs text-red-500 dark:text-red-400">{error || 'Failed to load'}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
          {issue?.url && (
            <a href={issue.url} target="_blank" rel="noopener noreferrer"
              className="p-1.5 hover:bg-gray-200 dark:hover:bg-slate-700 rounded transition-colors text-gray-400 dark:text-slate-400 hover:text-indigo-400"
              title="Open in Linear">
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3.5 w-3.5" />
            </a>
          )}
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 dark:hover:bg-slate-700 rounded transition-colors">
            <FontAwesomeIcon icon={faXmark} className="h-3.5 w-3.5 text-gray-400 dark:text-slate-400" />
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <FontAwesomeIcon icon={faSpinner} className="h-6 w-6 text-indigo-400 animate-spin" />
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg">
            <FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4 text-red-500 dark:text-red-400" />
            <p className="text-xs text-red-600 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* Content */}
      {issue && !loading && (
        <div className="divide-y divide-gray-200 dark:divide-slate-700/15">

          {/* Status bar */}
          <div className="px-5 py-3 flex items-center gap-3 flex-wrap bg-gray-50 dark:bg-slate-800/30">
            {issue.state && (
              <span className={`px-3 py-1 rounded-md text-xs font-bold border ${getLinearStatusColor(issue.state.name, issue.state.color)}`}>
                {issue.state.name}
              </span>
            )}
            {issue.priorityLabel && (
              <span className={`flex items-center gap-1 text-xs ${getPriorityInfo(issue.priority).color}`}>
                <FontAwesomeIcon icon={faBolt} className="h-3 w-3" /> {issue.priorityLabel}
              </span>
            )}
            {issue.estimate !== null && issue.estimate !== undefined && (
              <span className="text-xs text-purple-600 dark:text-purple-400 flex items-center gap-1">
                <FontAwesomeIcon icon={faBolt} className="h-3 w-3" /> {issue.estimate} pts
              </span>
            )}
            {issue.project && (
              <span className="text-xs text-cyan-600 dark:text-cyan-400 flex items-center gap-1">
                <FontAwesomeIcon icon={faLayerGroup} className="h-3 w-3" /> {issue.project.name}
              </span>
            )}
            {issue.cycle && (
              <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                <FontAwesomeIcon icon={faClock} className="h-3 w-3" /> {issue.cycle.name || `Cycle ${issue.cycle.number}`}
              </span>
            )}
            {issue.team && (
              <span className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-1">
                <FontAwesomeIcon icon={faUsers} className="h-3 w-3" /> {issue.team.name}
              </span>
            )}
          </div>

          {/* People */}
          <div>
            <SectionHeader id="people" title="People" icon={mkIcon(faUsers)} expanded={expandedSections.has('people')} onToggle={toggleSection} />
            {expandedSections.has('people') && (
              <div className="px-5 py-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <PersonBadge name={issue.assignee?.displayName || issue.assignee?.name} avatarUrl={issue.assignee?.avatarUrl} label="Assignee" />
                <PersonBadge name={issue.creator?.displayName || issue.creator?.name} avatarUrl={issue.creator?.avatarUrl} label="Creator" />
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <SectionHeader id="details" title="Details" icon={mkIcon(faFileLines)} expanded={expandedSections.has('details')} onToggle={toggleSection} />
            {expandedSections.has('details') && (
              <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Created</div>
                  <div className="text-xs text-gray-700 dark:text-slate-300">{formatDate(issue.createdAt)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Updated</div>
                  <div className="text-xs text-gray-700 dark:text-slate-300">{formatDate(issue.updatedAt)}</div>
                </div>
                {issue.startedAt && (
                  <div>
                    <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Started</div>
                    <div className="text-xs text-gray-700 dark:text-slate-300">{formatDate(issue.startedAt)}</div>
                  </div>
                )}
                {issue.completedAt && (
                  <div>
                    <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Completed</div>
                    <div className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                      <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3" /> {formatDate(issue.completedAt)}
                    </div>
                  </div>
                )}
                {issue.canceledAt && (
                  <div>
                    <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Cancelled</div>
                    <div className="text-xs text-red-600 dark:text-red-400">{formatDate(issue.canceledAt)}</div>
                  </div>
                )}
                {issue.dueDate && (
                  <div>
                    <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Due Date</div>
                    <div className="text-xs text-gray-700 dark:text-slate-300">{formatDateShort(issue.dueDate)}</div>
                  </div>
                )}
                {issue.cycle && (
                  <div>
                    <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-0.5">Cycle</div>
                    <div className="text-xs text-gray-700 dark:text-slate-300">
                      {issue.cycle.name || `Cycle ${issue.cycle.number}`}
                      {issue.cycle.startsAt && issue.cycle.endsAt && (
                        <span className="text-[10px] text-gray-400 dark:text-slate-500 ml-1">
                          ({formatDateShort(issue.cycle.startsAt)} — {formatDateShort(issue.cycle.endsAt)})
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {labels.length > 0 && (
                  <div className="col-span-2">
                    <div className="text-[10px] text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-1">Labels</div>
                    <div className="flex flex-wrap gap-1">
                      {labels.map((l: any) => (
                        <span key={l.id}
                          className="text-[10px] px-1.5 py-0.5 rounded border"
                          style={{
                            backgroundColor: l.color ? `${l.color}20` : undefined,
                            borderColor: l.color ? `${l.color}40` : undefined,
                            color: l.color || undefined,
                          }}>
                          {l.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <SectionHeader id="description" title="Description" icon={mkIcon(faFileLines)} expanded={expandedSections.has('description')} onToggle={toggleSection} />
            {expandedSections.has('description') && (
              <div className="px-5 py-3">
                {issue.description ? (
                  <div className="relative">
                    <div className={`${!descriptionExpanded ? 'max-h-[200px] overflow-hidden' : ''}`}>
                      <div
                        className="prose prose-sm dark:prose-invert max-w-none text-xs text-gray-700 dark:text-slate-300
                          [&_p]:mb-1.5 [&_a]:text-indigo-600 dark:[&_a]:text-indigo-400 [&_code]:text-[10px] [&_code]:bg-gray-100 dark:[&_code]:bg-slate-700 [&_code]:px-1 [&_code]:rounded
                          [&_pre]:bg-gray-100 dark:[&_pre]:bg-slate-700 [&_pre]:p-2 [&_pre]:rounded [&_pre]:text-[10px]
                          [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:mb-2
                          [&_li]:mb-0.5 [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 dark:[&_blockquote]:border-slate-600 [&_blockquote]:pl-3 [&_blockquote]:italic
                          [&_h1]:text-sm [&_h1]:font-semibold [&_h2]:text-xs [&_h2]:font-semibold [&_h3]:text-xs [&_h3]:font-medium
                          [&_hr]:border-gray-200 dark:[&_hr]:border-slate-700 [&_hr]:my-2"
                        dangerouslySetInnerHTML={{ __html: markdownToHtml(issue.description) }}
                      />
                    </div>
                    {issue.description.length > 500 && (
                      <div className="pt-2 text-center">
                        <button
                          onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                          className="text-[10px] font-medium px-4 py-1 rounded-md bg-gray-200/60 dark:bg-slate-700/60 text-gray-700 dark:text-slate-300 hover:bg-gray-300/70 dark:hover:bg-slate-600/70 transition-colors backdrop-blur-sm"
                        >
                          {descriptionExpanded ? 'Show less' : 'Show more'}
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 dark:text-slate-500 italic">No description</p>
                )}
              </div>
            )}
          </div>

          {/* Parent */}
          {issue.parent && (
            <div>
              <SectionHeader id="parent" title="Parent Issue" icon={mkIcon(faLayerGroup)} expanded={expandedSections.has('parent')} onToggle={toggleSection} />
              {expandedSections.has('parent') && (
                <div className="px-4 py-2">
                  <IssueLinkBtn
                    identifier={issue.parent.identifier}
                    summary={issue.parent.title}
                    status={issue.parent.state}
                    onClick={handleIssueClick}
                  />
                </div>
              )}
            </div>
          )}

          {/* Children / Sub-issues */}
          {children.length > 0 && (
            <div>
              <SectionHeader id="children" title="Sub-issues" icon={mkIcon(faLayerGroup)} count={children.length} expanded={expandedSections.has('children')} onToggle={toggleSection} />
              {expandedSections.has('children') && (
                <div className="px-4 py-2 space-y-0.5">
                  {children.map((child: any) => (
                    <div key={child.id} className="flex items-center gap-2">
                      <IssueLinkBtn
                        identifier={child.identifier}
                        summary={child.title}
                        status={child.state}
                        onClick={handleIssueClick}
                      />
                      {child.assignee && (
                        <span className="text-[10px] text-gray-400 dark:text-slate-500 flex-shrink-0">{child.assignee.displayName || child.assignee.name}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Relations */}
          {relations.length > 0 && (
            <div>
              <SectionHeader id="relations" title="Related Issues" icon={mkIcon(faLink)} count={relations.length} expanded={expandedSections.has('relations')} onToggle={toggleSection} />
              {expandedSections.has('relations') && (
                <div className="px-4 py-2 space-y-0.5">
                  {relations.map((rel: any) => (
                    <div key={rel.id} className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 dark:text-slate-500 capitalize flex-shrink-0 w-16">{rel.type?.replace(/_/g, ' ') || 'related'}</span>
                      {rel.relatedIssue && (
                        <IssueLinkBtn
                          identifier={rel.relatedIssue.identifier}
                          summary={rel.relatedIssue.title}
                          status={rel.relatedIssue.state}
                          onClick={handleIssueClick}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Attachments */}
          {attachments.length > 0 && (
            <div>
              <SectionHeader id="attachments" title="Attachments" icon={mkIcon(faLink)} count={attachments.length} expanded={expandedSections.has('attachments')} onToggle={toggleSection} />
              {expandedSections.has('attachments') && (
                <div className="px-5 py-2 space-y-1">
                  {attachments.map((att: any) => (
                    <a key={att.id} href={att.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700/30 transition-colors">
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="h-3 w-3 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <span className="text-xs text-indigo-600 dark:text-indigo-400">{att.title || att.url}</span>
                        {att.subtitle && <span className="text-[10px] text-gray-400 dark:text-slate-500 ml-2">{att.subtitle}</span>}
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Comments */}
          {comments.length > 0 && (
            <div>
              <SectionHeader id="comments" title="Comments" icon={mkIcon(faComment)} count={comments.length} expanded={expandedSections.has('comments')} onToggle={toggleSection} />
              {expandedSections.has('comments') && (
                <div className="px-5 py-2 space-y-3">
                  {comments.map((c: any) => (
                    <div key={c.id} className="bg-gray-50 dark:bg-slate-800/30 border border-gray-200 dark:border-slate-700/20 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {c.user?.avatarUrl ? (
                            <img src={c.user.avatarUrl} alt="" className="h-5 w-5 rounded-full" />
                          ) : (
                            <FontAwesomeIcon icon={faUser} className="h-4 w-4 text-gray-400 dark:text-slate-500" />
                          )}
                          <span className="text-xs font-medium text-gray-900 dark:text-white">{c.user?.displayName || c.user?.name || 'Unknown'}</span>
                        </div>
                        <span className="text-[10px] text-gray-400 dark:text-slate-500" title={formatDate(c.createdAt)}>{timeAgo(c.createdAt)}</span>
                      </div>
                      {c.body ? (
                        <div
                          className="prose prose-sm dark:prose-invert max-w-none text-xs text-gray-700 dark:text-slate-300
                            [&_p]:mb-1.5 [&_a]:text-indigo-600 dark:[&_a]:text-indigo-400 [&_code]:text-[10px] [&_code]:bg-gray-100 dark:[&_code]:bg-slate-700 [&_code]:px-1 [&_code]:rounded
                            [&_pre]:bg-gray-100 dark:[&_pre]:bg-slate-700 [&_pre]:p-2 [&_pre]:rounded [&_pre]:text-[10px]
                            [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-4 [&_ol]:mb-2
                            [&_li]:mb-0.5 [&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 dark:[&_blockquote]:border-slate-600 [&_blockquote]:pl-3 [&_blockquote]:italic
                            [&_h1]:text-sm [&_h1]:font-semibold [&_h2]:text-xs [&_h2]:font-semibold [&_h3]:text-xs [&_h3]:font-medium"
                          dangerouslySetInnerHTML={{ __html: markdownToHtml(c.body) }}
                        />
                      ) : (
                        <p className="text-xs text-gray-400 dark:text-slate-500 italic">Empty comment</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* History */}
          {history.length > 0 && (
            <div>
              <SectionHeader id="changelog" title="History" icon={mkIcon(faArrowRightArrowLeft)} count={history.length} expanded={expandedSections.has('changelog')} onToggle={toggleSection} />
              {expandedSections.has('changelog') && (
                <div className="px-4 py-2 space-y-1.5">
                  {history.map((entry: any) => {
                    const changes: { field: string; from: string | null; to: string | null }[] = [];
                    if (entry.fromState || entry.toState) {
                      changes.push({ field: 'Status', from: entry.fromState?.name || null, to: entry.toState?.name || null });
                    }
                    if (entry.fromAssignee || entry.toAssignee) {
                      changes.push({ field: 'Assignee', from: entry.fromAssignee?.name || null, to: entry.toAssignee?.name || null });
                    }
                    if (entry.fromPriority !== undefined && entry.toPriority !== undefined && entry.fromPriority !== entry.toPriority) {
                      changes.push({ field: 'Priority', from: getPriorityInfo(entry.fromPriority).label, to: getPriorityInfo(entry.toPriority).label });
                    }
                    if (entry.fromEstimate !== undefined && entry.toEstimate !== undefined && entry.fromEstimate !== entry.toEstimate) {
                      changes.push({ field: 'Estimate', from: entry.fromEstimate != null ? `${entry.fromEstimate}` : null, to: entry.toEstimate != null ? `${entry.toEstimate}` : null });
                    }

                    if (changes.length === 0) return null;

                    return (
                      <div key={entry.id} className="bg-gray-50 dark:bg-slate-800/20 border border-gray-200 dark:border-slate-700/15 rounded-lg p-2.5 hover:bg-gray-100 dark:hover:bg-slate-800/30 transition-colors">
                        <div className="flex items-center gap-2 mb-1.5">
                          <div className="h-5 w-5 rounded-full bg-gray-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                            {entry.actor?.avatarUrl ? (
                              <img src={entry.actor.avatarUrl} alt="" className="h-5 w-5 rounded-full" />
                            ) : (
                              <FontAwesomeIcon icon={faUser} className="h-3 w-3 text-gray-500 dark:text-slate-400" />
                            )}
                          </div>
                          <span className="text-[11px] font-medium text-gray-900 dark:text-white">{entry.actor?.name || 'System'}</span>
                          <span className="text-[10px] text-gray-500 dark:text-slate-600 ml-auto" title={formatDate(entry.createdAt)}>{timeAgo(entry.createdAt)}</span>
                        </div>
                        <div className="ml-7 space-y-1">
                          {changes.map((change, i) => (
                            <div key={i} className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-[10px] px-1.5 py-0.5 rounded border font-medium bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/20">
                                {change.field}
                              </span>
                              {change.from && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded border line-through opacity-60 ${
                                  change.field === 'Status' ? getLinearStatusColor(change.from) : 'text-gray-500 dark:text-slate-400 border-gray-300 dark:border-slate-600'
                                }`}>{change.from}</span>
                              )}
                              {(change.from || change.to) && <span className="text-[10px] text-gray-400 dark:text-slate-600">→</span>}
                              {change.to && (
                                <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                  change.field === 'Status' ? getLinearStatusColor(change.to) : 'text-gray-700 dark:text-slate-300 border-gray-300 dark:border-slate-600'
                                }`}>{change.to}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

        </div>
      )}
    </div>
  );
}

export default LinearIssueDetail;
